import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { FORUM_HERO } from "@/lib/assets";
import { MessagesSquare, Pin, MessageCircle, ArrowRight, Loader2, Search, X } from "lucide-react";

const CAT_ORDER = ["Spiritual", "Psychological", "Mental & Emotional Health", "Cultural & Identity", "Collective & Practical"];
const CAT_ACCENT = {
  "Spiritual": "text-amber-300 border-amber-400/30",
  "Psychological": "text-rose-300 border-rose-400/30",
  "Mental & Emotional Health": "text-teal-300 border-teal-400/30",
  "Cultural & Identity": "text-violet-300 border-violet-400/30",
  "Collective & Practical": "text-emerald-300 border-emerald-400/30",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 2592000) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Forum() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/forum/topics").then((r) => setTopics(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? topics.filter((t) => `${t.title} ${t.summary || ""} ${t.category || ""}`.toLowerCase().includes(q))
    : topics;

  const grouped = {};
  filtered.forEach((t) => { (grouped[t.category] = grouped[t.category] || []).push(t); });
  const ordered = [...CAT_ORDER.filter((c) => grouped[c]), ...Object.keys(grouped).filter((c) => !CAT_ORDER.includes(c))];

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="forum-page">
      <div className="relative border-b border-white/10 grain bg-surface overflow-hidden">
        <img src={FORUM_HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/85 to-onyx/55" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><MessagesSquare size={16} /> Community Forum</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">The Reasoning Circle</h1>
          <p className="text-bone/60 max-w-2xl text-lg">A space for the descendants of the Diaspora to share ideas and confront the spiritual, psychological and mental wounds of our journey — and to build the medicine together. Every voice matters. Speak freely, speak in love.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 space-y-14">
        {!loading && topics.length > 0 && (
          <div className="relative max-w-xl" data-testid="forum-search">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-bone/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="forum-search-input"
              placeholder="Search discussions by keyword or category..."
              className="w-full bg-surface border border-white/10 focus:border-gold/50 transition-colors pl-11 pr-11 py-3.5 text-bone placeholder:text-bone/40 focus:outline-none text-sm" />
            {query && (
              <button onClick={() => setQuery("")} data-testid="forum-search-clear" aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-bone/40 hover:text-gold transition-colors">
                <X size={18} />
              </button>
            )}
          </div>
        )}
        {loading ? (
          <div className="text-bone/50 flex items-center gap-2 py-20 justify-center"><Loader2 size={18} className="animate-spin" /> Gathering the circle...</div>
        ) : topics.length === 0 ? (
          <p className="text-bone/50 py-20 text-center" data-testid="forum-empty">No discussions yet.</p>
        ) : ordered.length === 0 ? (
          <p className="text-bone/50 py-16 text-center" data-testid="forum-no-results">No discussions match "{query}". Try a different word.</p>
        ) : (
          ordered.map((cat) => (
            <section key={cat} data-testid={`forum-cat-${cat.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <h2 className={`font-serif text-2xl text-bone mb-5 pb-2 border-b border-white/10`}>{cat}</h2>
              <div className="space-y-3">
                {grouped[cat].map((t) => {
                  const accent = CAT_ACCENT[cat] || "text-gold border-gold/30";
                  return (
                    <Link key={t.id} to={`/forum/${t.id}`} data-testid="forum-topic-link"
                      className="group block bg-surface border border-white/10 hover:border-gold/50 transition-colors p-5 md:p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {t.pinned && <span className="text-gold flex items-center gap-1 text-[11px] uppercase tracking-widest"><Pin size={12} /> Pinned</span>}
                            <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${accent}`}>{cat.split(" ")[0]}</span>
                          </div>
                          <h3 className="font-serif text-xl md:text-2xl text-bone group-hover:text-gold transition-colors leading-snug">{t.title}</h3>
                          <p className="text-bone/55 text-sm mt-2 line-clamp-2">{t.summary}</p>
                          <div className="flex items-center gap-4 text-xs text-bone/40 mt-3">
                            <span className="flex items-center gap-1.5" data-testid="forum-reply-count"><MessageCircle size={13} /> {t.reply_count} {t.reply_count === 1 ? "voice" : "voices"}</span>
                            <span>Active {timeAgo(t.last_activity)}</span>
                          </div>
                        </div>
                        <ArrowRight size={18} className="text-bone/30 group-hover:text-gold group-hover:translate-x-1 transition-all mt-1 shrink-0" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </main>
  );
}
